import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import moment from "moment";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import ReactPaginate from "react-paginate";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./CSS/ReportPremium.css";
import BPCLLogo from "./CSS/BPCL.png";
import FuelFlowLogo from "./CSS/fuelFlowLogo.png";
import IOCLLogo from "./CSS/IOCL.png";
import JIOBPLogo from "./CSS/JIOBP.png";
import HPCLLogo from "./CSS/HPCL.png";
import MRPLLogo from "./CSS/MRPL.png";
import NayaraLogo from "./CSS/NAYARA.png";
import ShellLogo from "./CSS/Shell.png";

const CustomerPassbook = () => {
  const token = localStorage.getItem("token");
  const readStoredValue = (key, fallback = "") => {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    try {
      const parsed = JSON.parse(raw);
      return parsed || fallback;
    } catch (err) {
      return String(raw).replace(/^["']+|["']+$/g, "").trim() || fallback;
    }
  };
  const bunkName =
    readStoredValue("pump_name") ||
    readStoredValue("bunkname") ||
    "Fuel Station";
  const bunkAddress =
    readStoredValue("bunk_address") ||
    readStoredValue("address") ||
    readStoredValue("station_address") ||
    "Address not set";
  const adminPhone =
    readStoredValue("admin_phone") ||
    readStoredValue("phone") ||
    readStoredValue("mobile") ||
    "Phone not set";
  const companyName =
    readStoredValue("company_name") ||
    readStoredValue("company") ||
    "FuelFlow";
  const companyKey = String(companyName || "FuelFlow")
    .replace(/[^a-z0-9]+/gi, " ")
    .trim()
    .toUpperCase();
  const companyLogoMap = {
    BPCL: BPCLLogo,
    HPCL: HPCLLogo,
    IOCL: IOCLLogo,
    "JIO BP": JIOBPLogo,
    MRPL: MRPLLogo,
    NAYARA: NayaraLogo,
    SHELL: ShellLogo,
  };
  const activeCompanyLogo = companyLogoMap[companyKey] || FuelFlowLogo;

  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [startDate, setStartDate] = useState(moment().startOf("month").format("YYYY-MM-DDTHH:mm"));
  const [endDate, setEndDate] = useState(moment().endOf("month").format("YYYY-MM-DDTHH:mm"));
  const [transactionFilter, setTransactionFilter] = useState("ALL");
  const [search, setSearch] = useState("");

  const [openingBalance, setOpeningBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({
    total_debit: 0,
    total_credit: 0,
    closing_balance: 0,
  });

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [tableSort, setTableSort] = useState({ key: null, direction: "asc" });
  const itemsPerPage = 10;

  const assetToDataUrl = async (assetUrl) => {
    try {
      const res = await fetch(assetUrl);
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      return null;
    }
  };

  const toExcelBase64 = (dataUrl) =>
    typeof dataUrl === "string" && dataUrl.includes(",")
      ? dataUrl.split(",")[1]
      : dataUrl;

  const toSqlDateTime = (value, boundary = "start") => {
    const m = moment(value);
    if (!m.isValid()) return null;
    return m.format("YYYY-MM-DD HH:mm:ss");
  };

  const saveBrandedPdf = async (title, bodyRows, filename, currentBalance) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const [fuelLogo, companyLogo] = await Promise.all([
      assetToDataUrl(FuelFlowLogo),
      assetToDataUrl(activeCompanyLogo),
    ]);
    const width = doc.internal.pageSize.getWidth();

    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, width, 34, "F");
    if (fuelLogo) doc.addImage(fuelLogo, "PNG", 12, 7, 18, 18);
    if (companyLogo) doc.addImage(companyLogo, "PNG", width - 30, 7, 18, 18);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(companyName, 34, 13);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(bunkName, 34, 19);
    doc.text(`${bunkAddress} | ${adminPhone}`, 34, 25);
    doc.text(title, 34, 31);
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Current Balance:", 14, 43);
    doc.text(`₹${Number(currentBalance || 0).toFixed(2)}`, 52, 43);

    autoTable(doc, {
      startY: 47,
      head: [["#", "Date & Time", "Vehicle No", "Receipt / Indent No", "Debit", "Credit", "Balance"]],
      body: bodyRows,
      styles: { fontSize: 8, cellPadding: 2.2, lineColor: [15, 23, 42], lineWidth: 0.35 },
      headStyles: {
        fillColor: [15, 23, 42],
        textColor: [255, 255, 255],
        lineColor: [15, 23, 42],
        lineWidth: 0.45,
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      tableLineColor: [15, 23, 42],
      tableLineWidth: 0.5,
    });

    doc.save(filename);
  };

  const selectedCustomer = customers.find(
    (c) => String(c.customer_id) === String(customerId)
  );

  const formatNumberCell = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const numeric = Number(value);
    return Number.isNaN(numeric) ? String(value) : numeric.toFixed(2);
  };

  const safeText = (value) => {
    if (value === null || value === undefined) return "";
    if (Array.isArray(value)) {
      return value.map(safeText).filter(Boolean).join(" | ").trim();
    }
    if (typeof value === "object") {
      return "";
    }
    return String(value).trim();
  };

  const renderUpdatedBy = (value) => {
    const text = safeText(value);
    return text || "-";
  };

  const deriveFuelDetails = (row) => {
    const description = String(row?.description || row?.particulars || "").trim();
    if (!description) {
      return { fuelType: "-", volume: null, price: null, amount: null };
    }

    const match = description.match(/Fuel Sale\s*-\s*(.+?)\s*-\s*([0-9.,]+)\s*L/i);
    const rawFuelType = row?.fuel_type || (match ? match[1] : "");
    const rawVolume = row?.volume || (match ? match[2] : "");
    const volume = rawVolume !== "" && rawVolume !== null && rawVolume !== undefined ? Number(rawVolume) : null;
    const amount = row?.amount !== undefined && row?.amount !== null ? Number(row.amount) : null;
    const price = row?.price !== undefined && row?.price !== null
      ? Number(row.price)
      : amount !== null && volume && volume > 0
        ? amount / volume
        : null;

    return {
      fuelType: rawFuelType || "-",
      volume,
      price,
      amount,
    };
  };

  const splitCommentParts = (rowOrValue) => {
    const row =
      rowOrValue && typeof rowOrValue === "object" ? rowOrValue : null;

    const explicitVehicleNo = safeText(row?.vehicle_no || row?.vehicleNo);
    const explicitReceiptNo = safeText(
      row?.receipt_or_indent_no || row?.receiptOrIndentNo
    );

    if (explicitVehicleNo || explicitReceiptNo) {
      return {
        vehicleNo: explicitVehicleNo || "-",
        receiptOrIndentNo: explicitReceiptNo || "-",
      };
    }

    const raw = safeText(row?.comment ?? rowOrValue);
    if (!raw) {
      return { vehicleNo: "-", receiptOrIndentNo: "-" };
    }

    const slashIndex = raw.indexOf("/");
    if (slashIndex === -1) {
      return {
        vehicleNo: raw || "-",
        receiptOrIndentNo: "-",
      };
    }

    return {
      vehicleNo: raw.slice(0, slashIndex).trim() || "-",
      receiptOrIndentNo: raw.slice(slashIndex + 1).trim() || "-",
    };
  };

  const getSortValue = (row, key) => {
    if (!row) return "";

    if (key === "transaction_datetime") {
      return row.transaction_datetime || "";
    }
    if (key === "vehicle_no") {
      return safeText(row.vehicle_no || splitCommentParts(row).vehicleNo || "-");
    }
    if (key === "receipt_or_indent_no") {
      return safeText(
        row.receipt_or_indent_no || splitCommentParts(row).receiptOrIndentNo || "-"
      );
    }
    if (key === "debit") return Number(row.debit || 0);
    if (key === "credit") return Number(row.credit || 0);
    if (key === "balance") return Number(row.balance || 0);

    const value = row[key];
    if (value === null || value === undefined) return "";
    return value;
  };

  const compareSortValues = (a, b) => {
    const numA = Number(a);
    const numB = Number(b);
    const aIsNum = a !== "" && a !== null && a !== undefined && Number.isFinite(numA);
    const bIsNum = b !== "" && b !== null && b !== undefined && Number.isFinite(numB);
    if (aIsNum && bIsNum) return numA - numB;

    const dateA = moment(a, moment.ISO_8601, true);
    const dateB = moment(b, moment.ISO_8601, true);
    if (dateA.isValid() && dateB.isValid()) return dateA.valueOf() - dateB.valueOf();

    return String(a ?? "").localeCompare(String(b ?? ""), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };

  const sortRowsByConfig = (rows = [], config = {}) => {
    const { key, direction } = config || {};
    if (!key) return rows;

    const dir = direction === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => {
      const result = compareSortValues(getSortValue(a, key), getSortValue(b, key));
      return result * dir;
    });
  };

  const handleTableSort = (key) => {
    setTableSort((prev) => {
      const nextDirection =
        prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction: nextDirection };
    });
    setPage(0);
  };

  const sortIndicator = (key) => {
    if (tableSort.key !== key) return "";
    return tableSort.direction === "asc" ? " ↑" : " ↓";
  };

  const fetchCustomers = async () => {
    try {
      const res = await api.get("/api/customer-passbook/customers", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rows = Array.isArray(res.data.data) ? res.data.data : [];
      const uniqueRows = Array.from(
        new Map(
          rows.map((row) => [String(row.customer_id), row])
        ).values()
      );
      setCustomers(uniqueRows);

      if (uniqueRows.length > 0 && !customerId) {
        setCustomerId(String(uniqueRows[0].customer_id));
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
    }
  };

  const fetchPassbook = async () => {
    if (!customerId) return;

    setLoading(true);
    try {
      const res = await api.get("/api/customer-passbook", {
          params: {
            customerId,
            startDate: moment(startDate).format("YYYY-MM-DD"),
            endDate: moment(endDate).format("YYYY-MM-DD"),
            startDateTime: toSqlDateTime(startDate),
            endDateTime: toSqlDateTime(endDate),
            summaryView: "0",
      },
      headers: { Authorization: `Bearer ${token}` },
    });

      setOpeningBalance(Number(res.data.opening_balance || 0));
      setTransactions(res.data.transactions || []);
      setSummary(
        res.data.summary || {
          total_debit: 0,
          total_credit: 0,
          closing_balance: 0,
        }
      );
      setPage(0);
    } catch (err) {
      console.error("Error fetching passbook:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (customerId) fetchPassbook();
    // eslint-disable-next-line
  }, [customerId]);

  useEffect(() => {
    if (customerId) fetchPassbook();
    // eslint-disable-next-line
  }, [startDate, endDate]);

  const displayTransactions = useMemo(() => {
    const paymentBuckets = new Map();
    const emittedPaymentMinutes = new Set();

    (transactions || []).forEach((row) => {
      if (String(row?.source_type || "").toUpperCase() !== "PAYMENT") return;
      const minuteKey = row?.transaction_datetime ? String(row.transaction_datetime).slice(0, 16) : null;
      if (!minuteKey) return;

      if (!paymentBuckets.has(minuteKey)) {
        paymentBuckets.set(minuteKey, {
          minuteKey,
          credit: 0,
          count: 0,
          comments: new Set(),
          modes: new Set(),
          refs: new Set(),
          vehicleNos: new Set(),
          receiptNos: new Set(),
          firstRow: row,
          lastRow: row,
        });
      }

      const bucket = paymentBuckets.get(minuteKey);
      bucket.credit += Number(row.credit || 0);
      bucket.count += 1;
      bucket.lastRow = row;
      if (row.comment) bucket.comments.add(String(row.comment).trim());
      if (row.payment_mode) bucket.modes.add(String(row.payment_mode).trim());
      if (row.reference_no) bucket.refs.add(String(row.reference_no).trim());
      const parts = splitCommentParts(row);
      if (parts.vehicleNo && parts.vehicleNo !== "-") bucket.vehicleNos.add(parts.vehicleNo);
      if (parts.receiptOrIndentNo && parts.receiptOrIndentNo !== "-") {
        bucket.receiptNos.add(parts.receiptOrIndentNo);
      }
    });

    return (transactions || []).flatMap((row) => {
      if (String(row?.source_type || "").toUpperCase() !== "PAYMENT") {
        return {
          ...row,
          vehicle_no: splitCommentParts(row).vehicleNo,
          receipt_or_indent_no: splitCommentParts(row).receiptOrIndentNo,
        };
      }

      const minuteKey = row?.transaction_datetime ? String(row.transaction_datetime).slice(0, 16) : null;
      if (!minuteKey || emittedPaymentMinutes.has(minuteKey)) return [];

      emittedPaymentMinutes.add(minuteKey);
      const bucket = paymentBuckets.get(minuteKey);
      if (!bucket) {
        return {
          ...row,
          vehicle_no: splitCommentParts(row).vehicleNo,
          receipt_or_indent_no: splitCommentParts(row).receiptOrIndentNo,
        };
      }

      const firstRow = bucket.firstRow || row;
      return {
        ...row,
        source_id: bucket.minuteKey,
        transaction_datetime: firstRow.transaction_datetime,
        transaction_type:
          bucket.count > 1
            ? `PAYMENT RECEIVED (${bucket.count})`
            : firstRow.transaction_type || "PAYMENT RECEIVED",
        description:
          bucket.count > 1
            ? `${bucket.count} settlements consolidated`
            : firstRow.description || "Customer Payment",
        particulars: firstRow.particulars || "Payment Received",
        payment_mode:
          Array.from(bucket.modes).filter(Boolean).join(", ") ||
          firstRow.payment_mode ||
          "-",
        reference_no:
          Array.from(bucket.refs).filter(Boolean).join(", ") ||
          firstRow.reference_no ||
          "-",
        debit: 0,
        credit: Number(bucket.credit.toFixed(2)),
        balance: Number(Number(bucket.lastRow?.balance ?? row.balance ?? 0).toFixed(2)),
        comment:
          Array.from(bucket.comments).filter(Boolean).join(" | ") ||
          firstRow.comment ||
          "",
        vehicle_no: Array.from(bucket.vehicleNos).filter(Boolean).join(" | ") || "-",
        receipt_or_indent_no: Array.from(bucket.receiptNos).filter(Boolean).join(" | ") || "-",
      };
    });
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    const q = search.trim().toLowerCase();

    return displayTransactions.filter((r) => {
      const debit = Number(r.debit || 0);
      const credit = Number(r.credit || 0);

      if (transactionFilter === "DEBIT" && debit <= 0) return false;
      if (transactionFilter === "CREDIT" && credit <= 0) return false;

      if (!q) return true;

      const text = [
        r.transaction_datetime,
        r.transaction_type,
        r.description,
        r.particulars,
        r.comment,
        r.vehicle_no,
        r.receipt_or_indent_no,
        r.fuel_type,
        r.volume,
        r.price,
        r.amount,
        r.debit,
        r.credit,
        r.balance,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return text.includes(q);
    });
  }, [displayTransactions, search, transactionFilter]);

  const sortedTransactions = useMemo(
    () => sortRowsByConfig(filteredTransactions, tableSort),
    [filteredTransactions, tableSort]
  );

  const offset = page * itemsPerPage;
  const currentRows = sortedTransactions.slice(offset, offset + itemsPerPage);
  const pageCount = Math.ceil(sortedTransactions.length / itemsPerPage) || 0;

  const exportToExcel = async () => {
    if (!sortedTransactions.length) return;

    const customerName = selectedCustomer?.customer_name || "Customer";

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Passbook");
    const [fuelLogo, companyLogo] = await Promise.all([
      assetToDataUrl(FuelFlowLogo),
      assetToDataUrl(activeCompanyLogo),
    ]);

    worksheet.getRow(1).height = 24;
    worksheet.getRow(2).height = 24;

    const headers = [
      companyName,
      bunkName,
      bunkAddress,
      `Admin Phone: ${adminPhone}`,
      "Customer Passbook",
      `Customer: ${customerName}`,
      `Period: ${startDate} to ${endDate}`,
      `Opening Balance: ₹${Number(openingBalance || 0).toFixed(2)}`,
      `Generated On: ${moment().format("DD-MM-YYYY hh:mm A")}`,
    ];

    headers.forEach((line, index) => {
      const row = worksheet.getRow(index + 3);
      row.getCell(1).value = line;
      worksheet.mergeCells(index + 3, 1, index + 3, 7);
      const cell = row.getCell(1);
      cell.font = {
        bold: index <= 4,
        size: index === 4 ? 14 : index <= 3 ? 12 : 10,
        color: { argb: "FF0F172A" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: index <= 3 ? "FFF8FAFC" : "FFFFFFFF" },
      };
    });

    const columns = ["#", "Date & Time", "Vehicle No", "Receipt / Indent No", "Debit (₹)", "Credit (₹)", "Balance (₹)"];
    worksheet.addRow([]);
    const headerRow = worksheet.addRow(columns);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF0F172A" },
    };

    sortedTransactions.forEach((r, idx) => {
      const commentParts = splitCommentParts(r);
      worksheet.addRow([
        idx + 1,
        r.transaction_datetime ? moment(r.transaction_datetime).format("DD-MM-YYYY hh:mm A") : "",
        commentParts.vehicleNo,
        commentParts.receiptOrIndentNo,
        formatNumberCell(r.debit),
        formatNumberCell(r.credit),
        formatNumberCell(r.balance),
      ]);
    });

    worksheet.columns = [6, 20, 18, 28, 14, 14, 16].map((width, index) => ({
      key: columns[index],
      width,
    }));

    if (fuelLogo) {
      const logoId = workbook.addImage({ base64: toExcelBase64(fuelLogo), extension: "png" });
      worksheet.addImage(logoId, "A1:B2");
    }
    if (companyLogo) {
      const logoId = workbook.addImage({ base64: toExcelBase64(companyLogo), extension: "png" });
      worksheet.addImage(logoId, "F1:F2");
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `CustomerPassbook_${customerName}_${moment(startDate).format("DDMMYYYY")}_${moment(endDate).format("DDMMYYYY")}.xlsx`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const exportToPDF = async () => {
    if (!sortedTransactions.length) return;

    const customerName = selectedCustomer?.customer_name || "Customer";
    const bodyRows = filteredTransactions.map((r, idx) => {
      const commentParts = splitCommentParts(r);
      return [
        idx + 1,
        r.transaction_datetime ? moment(r.transaction_datetime).format("DD-MM-YYYY hh:mm A") : "",
        commentParts.vehicleNo,
        commentParts.receiptOrIndentNo,
        formatNumberCell(r.debit),
        formatNumberCell(r.credit),
        formatNumberCell(r.balance),
      ];
    });

    try {
      await saveBrandedPdf(
        `Customer Passbook - ${customerName} | Period: ${startDate} to ${endDate}`,
        bodyRows,
        `CustomerPassbook_${customerName}_${moment(startDate).format("DDMMYYYY")}_${moment(endDate).format("DDMMYYYY")}.pdf`,
        summary.closing_balance
      );
    } catch (err) {
      console.error("Error exporting passbook PDF:", err);
    }
  };

  return (
    <div className="customer-passbook">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <div>
          <h4 className="mb-1">Customer Passbook</h4>
          <small className="text-muted">
            Bank-style consolidated credit, debit and payment summaries
          </small>
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn btn-success"
            onClick={exportToExcel}
          disabled={!sortedTransactions.length}
          >
            Export Excel
          </button>
          <button
            className="btn btn-outline-danger"
            onClick={exportToPDF}
            disabled={!sortedTransactions.length}
          >
            Export PDF
          </button>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-3">
              <label className="form-label">Customer</label>
              <select
                className="form-select"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
              >
                <option value="">Select Customer</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.customer_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Start Date &amp; Time</label>
              <input
                type="datetime-local"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="col-md-2">
              <label className="form-label">End Date &amp; Time</label>
              <input
                type="datetime-local"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div className="col-md-2">
              <label className="form-label">Type</label>
              <select
                className="form-select"
                value={transactionFilter}
                onChange={(e) => {
                  setTransactionFilter(e.target.value);
                  setPage(0);
                }}
              >
                <option value="ALL">All</option>
                <option value="DEBIT">Debit</option>
                <option value="CREDIT">Credit</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Search</label>
              <input
                className="form-control"
                placeholder="Search date or amount..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
            </div>

            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-primary w-100"
                onClick={fetchPassbook}
                disabled={loading || !customerId}
              >
                {loading ? "Loading..." : "Generate"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-6 col-md-3">
          <div className="card border-dark h-100">
            <div className="card-body py-2">
              <small className="text-muted d-block">Opening Balance</small>
              <strong>₹{Number(openingBalance || 0).toFixed(2)}</strong>
            </div>
          </div>
        </div>

        <div className="col-6 col-md-3">
          <div className="card border-danger h-100">
            <div className="card-body py-2">
              <small className="text-muted d-block">Credit Given</small>
              <strong className="text-danger">
                ₹{Number(summary.total_debit || 0).toFixed(2)}
              </strong>
            </div>
          </div>
        </div>

        <div className="col-6 col-md-3">
          <div className="card border-success h-100">
            <div className="card-body py-2">
              <small className="text-muted d-block">Payment Received</small>
              <strong className="text-success">
                ₹{Number(summary.total_credit || 0).toFixed(2)}
              </strong>
            </div>
          </div>
        </div>

        <div className="col-6 col-md-3">
          <div className="card border-primary h-100">
            <div className="card-body py-2">
              <small className="text-muted d-block">Closing Balance</small>
              <strong className="text-primary">
                ₹{Number(summary.closing_balance || 0).toFixed(2)}
              </strong>
            </div>
          </div>
        </div>
      </div>

      <div className="alert alert-light border mb-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <strong>Current Balance</strong>
          <div className="text-muted small">The latest closing balance for the selected customer</div>
        </div>
        <strong className="fs-5 text-primary">₹{Number(summary.closing_balance || 0).toFixed(2)}</strong>
      </div>

      {selectedCustomer && (
        <div className="alert alert-light border mb-3">
          <strong>{selectedCustomer.customer_name}</strong>
          {selectedCustomer.phone ? <> | Phone: {selectedCustomer.phone}</> : null}
          {selectedCustomer.vehicle_no ? (
            <> | Vehicle: {selectedCustomer.vehicle_no}</>
          ) : null}
        </div>
      )}

      <div className="passbook-table-shell">
        <div className="table-responsive passbook-table-scroll">
        <table className="table table-bordered table-striped align-middle passbook-table">
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th
                role="button"
                tabIndex={0}
                onDoubleClick={() => handleTableSort("transaction_datetime")}
                title="Double-click to sort"
              >
                Date &amp; Time{sortIndicator("transaction_datetime")}
              </th>
              <th
                role="button"
                tabIndex={0}
                onDoubleClick={() => handleTableSort("vehicle_no")}
                title="Double-click to sort"
              >
                Vehicle No{sortIndicator("vehicle_no")}
              </th>
              <th
                role="button"
                tabIndex={0}
                onDoubleClick={() => handleTableSort("receipt_or_indent_no")}
                title="Double-click to sort"
              >
                Receipt / Indent No{sortIndicator("receipt_or_indent_no")}
              </th>
              <th
                className="text-end"
                role="button"
                tabIndex={0}
                onDoubleClick={() => handleTableSort("debit")}
                title="Double-click to sort"
              >
                Debit (₹){sortIndicator("debit")}
              </th>
              <th
                className="text-end"
                role="button"
                tabIndex={0}
                onDoubleClick={() => handleTableSort("credit")}
                title="Double-click to sort"
              >
                Credit (₹){sortIndicator("credit")}
              </th>
              <th
                className="text-end"
                role="button"
                tabIndex={0}
                onDoubleClick={() => handleTableSort("balance")}
                title="Double-click to sort"
              >
                Balance (₹){sortIndicator("balance")}
              </th>
            </tr>
          </thead>

          <tbody>
            {currentRows.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center">
                  {loading ? "Loading..." : "No passbook transactions found"}
                </td>
              </tr>
            ) : (
              currentRows.map((r, idx) => {
                const commentParts = splitCommentParts(r);
                return (
                  <tr key={`${r.source_type}-${r.source_id}-${idx}`}>
                    <td>{offset + idx + 1}</td>
                    <td>
                      {r.transaction_datetime
                        ? moment(r.transaction_datetime).format("DD-MM-YYYY hh:mm A")
                        : "-"}
                    </td>
                    <td>{commentParts.vehicleNo}</td>
                    <td>{commentParts.receiptOrIndentNo || "-"}</td>
                    <td className="text-end text-danger">
                      {formatNumberCell(r.debit)}
                    </td>
                    <td className="text-end text-success">
                      {formatNumberCell(r.credit)}
                    </td>
                    <td className="text-end fw-bold">
                      <div>{formatNumberCell(r.balance)}</div>
                      <div className="passbook-updated-by">
                        By: {renderUpdatedBy(r.updated_by_name)}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      {pageCount > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <ReactPaginate
            previousLabel={"← Prev"}
            nextLabel={"Next →"}
            pageCount={pageCount}
            forcePage={page}
            onPageChange={(e) => setPage(e.selected)}
            containerClassName={"pagination"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            nextClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextLinkClassName={"page-link"}
            activeClassName={"active"}
          />
        </div>
      )}
    </div>
  );
};

export default CustomerPassbook;
